import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Camera, Upload, ScanLine, Sparkles, Check, RotateCcw, Crosshair, Smartphone, AlertTriangle, Aperture, Maximize2, Minimize2, X, MoveHorizontal, Radar,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { useMeasurements } from "../context/MeasurementsContext";
import { useVehicle } from "../context/VehicleContext";
import { api } from "../lib/api";
import { limitMsg } from "../lib/api";
import { H2_SPECS } from "../lib/specs";
import { AccuracyNote } from "../components/AccuracyNote";
import { useTranslation } from "react-i18next";

const CORNERS = [
  { id: "FL", label: "Front Left" },
  { id: "FR", label: "Front Right" },
  { id: "RL", label: "Rear Left" },
  { id: "RR", label: "Rear Right" },
];

const dataURLtoBlob = async (dataUrl) => (await fetch(dataUrl)).blob();

export default function VisualScan() {
  const { t } = useTranslation();
  const { setCornerAngle, setPhoto } = useMeasurements();
  const { specs } = useVehicle();
  const navigate = useNavigate();

  const [mode, setMode] = useState("camera"); // camera | upload
  const [corner, setCorner] = useState("FL");
  const [angleType, setAngleType] = useState("camber");
  const [guideAngle, setGuideAngle] = useState(0);
  const [captured, setCaptured] = useState(null); // dataURL
  const [cameraError, setCameraError] = useState(null);
  const [deviceTilt, setDeviceTilt] = useState(null);
  const [tiltEnabled, setTiltEnabled] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [axle, setAxle] = useState("front");
  const [sweeping, setSweeping] = useState(false);
  const [sweepProgress, setSweepProgress] = useState(0);
  const [sweepSpan, setSweepSpan] = useState(0);
  const [axleResult, setAxleResult] = useState(null);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const svgRef = useRef(null);
  const viewportRef = useRef(null);
  const sweepRef = useRef({ frames: [], interval: null, baseAlpha: null, lastAlpha: null, span: 0 });

  const isRear = corner === "RL" || corner === "RR";

  // ---- camera lifecycle ----
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("This browser blocks camera access. Open the app in a normal browser tab (Chrome/Safari) and try again, or use Upload mode.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
        setCameraError("Camera permission is blocked. Click the camera/lock icon in your browser's address bar, choose \u201CAllow\u201D, then press Retry. If you're viewing inside the embedded preview, open the app in its own browser tab first.");
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "OverconstrainedError") {
        setCameraError("No camera device was found on this machine. Use Upload mode, or switch to a phone/laptop with a camera.");
      } else if (name === "NotReadableError") {
        setCameraError("The camera is in use by another app. Close other apps using the camera and press Retry.");
      } else {
        setCameraError("Camera unavailable. Use Upload mode, or press Retry.");
      }
    }
  }, []);

  useEffect(() => {
    if (mode === "camera" && !captured) startCamera();
    else stopCamera();
    return stopCamera;
  }, [mode, captured, startCamera, stopCamera]);

  // ---- fullscreen ----
  const toggleFullscreen = useCallback(async () => {
    const el = viewportRef.current;
    try {
      if (!document.fullscreenElement) {
        if (el?.requestFullscreen) await el.requestFullscreen();
        else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
        setFullscreen(true);
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        setFullscreen(false);
      }
    } catch {
      // Native fullscreen can be blocked (e.g. iOS Safari) — fall back to the CSS overlay.
      setFullscreen((f) => !f);
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => { if (!document.fullscreenElement) setFullscreen(false); };
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  // ---- device tilt (gravity camber gauge) ----
  const handleOrientation = useCallback((e) => {
    if (e.gamma != null) setDeviceTilt(e.gamma);
  }, []);

  const enableTilt = async () => {
    try {
      if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
        const res = await DeviceOrientationEvent.requestPermission();
        if (res !== "granted") { toast.error("Motion sensor permission denied"); return; }
      }
      window.addEventListener("deviceorientation", handleOrientation);
      setTiltEnabled(true);
      toast.success("Tilt sensor active", { description: "Rest your phone flat against the wheel face." });
    } catch {
      toast.error("Tilt sensor not available on this device");
    }
  };
  useEffect(() => () => window.removeEventListener("deviceorientation", handleOrientation), [handleOrientation]);

  // ---- capture frame ----
  const captureFrame = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { toast.error("Camera not ready"); return; }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    setCaptured(canvas.toDataURL("image/jpeg", 0.85));
    stopCamera();
    toast("Frame captured — fine-tune the guide or run AI detect");
  };

  const onUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCaptured(reader.result);
    reader.readAsDataURL(file);
  };

  const retake = () => {
    setCaptured(null);
    setAiResult(null);
    if (mode === "camera") startCamera();
  };

  // ---- drag-to-rotate guide ----
  const pointerAngle = (clientX, clientY) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // viewBox 0 0 300 400, pivot at (150, 380)
    const x = ((clientX - rect.left) / rect.width) * 300;
    const y = ((clientY - rect.top) / rect.height) * 400;
    const dx = x - 150;
    const dy = 380 - y; // upward positive
    let deg = (Math.atan2(dx, dy) * 180) / Math.PI;
    deg = Math.max(-25, Math.min(25, deg));
    setGuideAngle(Math.round(deg * 100) / 100);
  };

  // ---- AI detect ----
  const runAI = async () => {
    if (!captured) { toast.error("Capture or upload a photo first"); return; }
    setAnalyzing(true);
    setAiResult(null);
    try {
      const { data } = await api.post("/scan/analyze", { image_base64: captured, angleType });
      setAiResult(data);
      const val = angleType === "toe" ? data.toe_deg : data.camber_deg;
      if (val != null) {
        setGuideAngle(Math.round(val * 100) / 100);
        toast.success("AI estimate applied to guide", { description: `${data.reasoning || ""}`.slice(0, 80) });
      } else {
        toast.warning("AI could not read that angle clearly — adjust the guide manually");
      }
    } catch (err) {
      toast.error(limitMsg(err) || "AI detection failed");
    } finally {
      setAnalyzing(false);
    }
  };

  // ---- apply to calculator ----
  const applyReading = async () => {
    setApplying(true);
    try {
      let photoRef = null;
      if (captured) {
        const blob = await dataURLtoBlob(captured);
        const fd = new FormData();
        fd.append("file", blob, `${corner}-${angleType}.jpg`);
        const { data } = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photoRef = { ...data, type: angleType };
        setPhoto(corner, photoRef);
      }
      setCornerAngle(corner, angleType, guideAngle);
      toast.success(`${corner} ${angleType} set to ${guideAngle.toFixed(2)}°`, { description: "Applied to the calculator." });
      navigate("/");
    } catch (err) {
      toast.error(limitMsg(err) || "Could not save the scan photo");
    } finally {
      setApplying(false);
    }
  };

  // ---- panoramic axle sweep ----
  const SWEEP_TARGET_FRAMES = 14;
  const SWEEP_MIN_DIFF = 5; // skip frames with almost no movement (prevents repeated/duplicated slices)

  // cheap 12x12 grayscale fingerprint used to detect whether the view actually changed
  const frameFingerprint = (cv) => {
    const t = document.createElement("canvas");
    t.width = 12; t.height = 12;
    const tc = t.getContext("2d");
    tc.drawImage(cv, 0, 0, 12, 12);
    const d = tc.getImageData(0, 0, 12, 12).data;
    const out = [];
    for (let i = 0; i < d.length; i += 4) out.push((d[i] + d[i + 1] + d[i + 2]) / 3);
    return out;
  };
  const fingerprintDiff = (a, b) => {
    let s = 0;
    for (let i = 0; i < a.length; i++) s += Math.abs(a[i] - b[i]);
    return s / a.length;
  };

  const onSweepOrientation = (e) => {
    if (e.alpha == null) return;
    const s = sweepRef.current;
    if (s.baseAlpha == null) { s.baseAlpha = e.alpha; s.lastAlpha = e.alpha; return; }
    let d = e.alpha - s.lastAlpha;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    s.span += Math.abs(d);
    s.lastAlpha = e.alpha;
    setSweepSpan(Math.round(s.span));
  };

  const finishSweep = () => {
    const s = sweepRef.current;
    if (s.interval) { clearInterval(s.interval); s.interval = null; }
    window.removeEventListener("deviceorientation", onSweepOrientation);
    setSweeping(false);
    const frames = s.frames;
    if (!frames.length) { toast.error("No frames captured — try again"); return; }
    if (frames.length === 1) {
      // only one clean frame — use it as-is, no stitching needed
      setCaptured(frames[0].toDataURL("image/jpeg", 0.9));
      stopCamera();
      toast.success("Axle shot captured — run AI Read Axle");
      return;
    }
    // Build ONE clean axle image from the two ENDPOINTS of the sweep: the left
    // half comes from the first frame (left wheel) and the right half from the
    // last frame (right wheel), feathered at the seam. This eliminates the
    // repeated / "multiple wheels" artefact that slit-scan tiling produced.
    const left = frames[0];
    const right = frames[frames.length - 1];
    const w = Math.min(left.width, right.width);
    const h = Math.min(left.height, right.height);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(left, 0, 0, w, h);
    const mid = w / 2;
    const feather = Math.max(20, Math.floor(w * 0.06));
    const tmp = document.createElement("canvas");
    tmp.width = w; tmp.height = h;
    const tc = tmp.getContext("2d");
    tc.drawImage(right, 0, 0, w, h);
    // erase the left portion of the right frame with a feathered seam at center
    const grad = tc.createLinearGradient(mid - feather, 0, mid + feather, 0);
    grad.addColorStop(0, "rgba(0,0,0,1)");
    grad.addColorStop(1, "rgba(0,0,0,0)");
    tc.globalCompositeOperation = "destination-out";
    tc.fillStyle = grad;
    tc.fillRect(0, 0, mid + feather, h);
    tc.globalCompositeOperation = "source-over";
    ctx.drawImage(tmp, 0, 0);
    setCaptured(canvas.toDataURL("image/jpeg", 0.9));
    stopCamera();
    toast.success("Axle image built — run AI Read Axle");
  };

  const captureSweepFrame = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth;
    canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0);
    const s = sweepRef.current;
    const fp = frameFingerprint(canvas);
    // drop near-identical frames (camera held still) so the panorama never repeats slices
    if (s.lastFp && fingerprintDiff(fp, s.lastFp) < SWEEP_MIN_DIFF) return;
    s.lastFp = fp;
    s.frames.push(canvas);
    const n = s.frames.length;
    setSweepProgress(Math.min(1, n / SWEEP_TARGET_FRAMES));
    if (n >= SWEEP_TARGET_FRAMES) finishSweep();
  };

  const startSweep = async () => {
    if (cameraError) { toast.error("Camera needed for the sweep"); return; }
    sweepRef.current = { frames: [], interval: null, baseAlpha: null, lastAlpha: null, span: 0, lastFp: null };
    setAxleResult(null); setCaptured(null); setSweepProgress(0); setSweepSpan(0);
    setSweeping(true);
    try {
      if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
        await DeviceOrientationEvent.requestPermission().catch(() => {});
      }
    } catch { /* ignore */ }
    window.addEventListener("deviceorientation", onSweepOrientation);
    sweepRef.current.interval = setInterval(captureSweepFrame, 280);
    toast("Pan slowly across the axle — left wheel to right wheel");
  };

  useEffect(() => () => {
    if (sweepRef.current.interval) clearInterval(sweepRef.current.interval);
    window.removeEventListener("deviceorientation", onSweepOrientation);
  }, []);

  const runAxleAI = async () => {
    if (!captured) { toast.error("Build a panorama first"); return; }
    setAnalyzing(true); setAxleResult(null);
    try {
      const { data } = await api.post("/scan/analyze-axle", { image_base64: captured, axle, sweepSpanDeg: sweepSpan || null });
      setAxleResult(data);
      toast.success("Axle analyzed", { description: `${data.reasoning || ""}`.slice(0, 80) });
    } catch (err) {
      toast.error(limitMsg(err) || "AI axle read failed");
    } finally {
      setAnalyzing(false);
    }
  };

  const applyAxle = async () => {
    if (!axleResult) { toast.error("Run AI Read Axle first"); return; }
    const hasAny = axleResult.left_camber_deg != null || axleResult.right_camber_deg != null || axleResult.total_toe_deg != null;
    if (!hasAny) {
      toast.warning("AI couldn't read the axle clearly", { description: "Retake the sweep with both wheels in frame, or enter values manually." });
      return;
    }
    setApplying(true);
    try {
      if (captured) {
        const blob = await dataURLtoBlob(captured);
        const fd = new FormData();
        fd.append("file", blob, `${axle}-pano.jpg`);
        const { data } = await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhoto(axle === "front" ? "FRONT" : "REAR", { ...data, type: "axle-pano" });
      }
      const Lc = axle === "front" ? "FL" : "RL";
      const Rc = axle === "front" ? "FR" : "RR";
      if (axleResult.left_camber_deg != null) setCornerAngle(Lc, "camber", axleResult.left_camber_deg);
      if (axleResult.right_camber_deg != null) setCornerAngle(Rc, "camber", axleResult.right_camber_deg);
      if (axleResult.total_toe_deg != null) setCornerAngle(Lc, "toe", axleResult.total_toe_deg);
      toast.success(`${axle === "front" ? "Front" : "Rear"} axle applied`, { description: "Camber on both wheels + total toe set." });
      navigate("/");
    } catch {
      toast.error("Could not apply axle reading");
    } finally {
      setApplying(false);
    }
  };

  const retakeSweep = () => {
    setCaptured(null); setAxleResult(null); setSweepProgress(0); setSweepSpan(0);
    startCamera();
  };

  const spec = isRear ? specs.rear : specs.front;
  const activeSpec = angleType === "toe" ? spec.totalToe : (angleType === "caster" ? specs.front.caster : spec.camber);
  const inSpec = guideAngle >= activeSpec.min && guideAngle <= activeSpec.max;

  // guide line endpoint
  const rad = (guideAngle * Math.PI) / 180;
  const L = 300;
  const ex = 150 + L * Math.sin(rad);
  const ey = 380 - L * Math.cos(rad);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8 animate-rise">
        <div className="flex items-center gap-2 font-mono text-xs text-amber tracking-widest uppercase">
          <ScanLine size={14} /> {t("nav.scan")}
        </div>
        <h1 className="font-heading font-black uppercase tracking-wider text-3xl sm:text-4xl lg:text-5xl mt-2">{t("scan.title")}</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-2xl">
          {t("scan.subtitle")}
        </p>
      </div>

      <AccuracyNote compact className="mb-6 max-w-2xl" />

      {/* controls */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-4 space-y-5">
          {/* mode toggle */}
          <div className="rounded-md bg-surface border border-steel p-4">
            <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-2">Scan Source</div>
            <div className="grid grid-cols-3 gap-2">
              <Button data-testid="mode-camera" onClick={() => { setMode("camera"); setCaptured(null); setAiResult(null); setAxleResult(null); startCamera(); }}
                className={`font-mono text-[11px] uppercase tracking-widest px-2 ${mode === "camera" ? "bg-amber text-ink" : "bg-elevated text-slate-300 hover:bg-steel"}`}>
                <Camera size={15} /> Camera
              </Button>
              <Button data-testid="mode-pano" onClick={() => { setMode("pano"); setCaptured(null); setAiResult(null); setAxleResult(null); startCamera(); }}
                className={`font-mono text-[11px] uppercase tracking-widest px-2 ${mode === "pano" ? "bg-amber text-ink" : "bg-elevated text-slate-300 hover:bg-steel"}`}>
                <MoveHorizontal size={15} /> Pano
              </Button>
              <Button data-testid="mode-upload" onClick={() => { setMode("upload"); setCaptured(null); setAiResult(null); setAxleResult(null); }}
                className={`font-mono text-[11px] uppercase tracking-widest px-2 ${mode === "upload" ? "bg-amber text-ink" : "bg-elevated text-slate-300 hover:bg-steel"}`}>
                <Upload size={15} /> Upload
              </Button>
            </div>
            {mode === "pano" && (
              <p className="font-mono text-[10px] text-slate-500 mt-2 leading-relaxed">
                Panoramic axle sweep: pan the camera from the left wheel to the right wheel; AI reads both wheels + total toe at once.
              </p>
            )}
          </div>

          {/* target: corner (single wheel) or axle (panoramic sweep) */}
          {mode === "pano" ? (
            <div className="rounded-md bg-surface border border-steel p-4">
              <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-2">Target Axle</div>
              <div className="grid grid-cols-2 gap-2">
                {["front", "rear"].map((a) => (
                  <button key={a} data-testid={`axle-${a}`} onClick={() => setAxle(a)}
                    className={`py-2 rounded-sm font-mono text-xs uppercase tracking-widest border transition-colors ${axle === a ? "bg-amber text-ink border-amber font-bold" : "bg-ink text-slate-400 border-steel hover:border-amber/50"}`}>
                    {a} axle
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-md bg-surface border border-steel p-4">
              <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-2">Target Corner</div>
              <div className="grid grid-cols-2 gap-2">
                {CORNERS.map((c) => (
                  <button key={c.id} data-testid={`corner-${c.id}`} onClick={() => setCorner(c.id)}
                    className={`py-2 rounded-sm font-mono text-xs uppercase tracking-widest border transition-colors ${corner === c.id ? "bg-amber text-ink border-amber font-bold" : "bg-ink text-slate-400 border-steel hover:border-amber/50"}`}>
                    {c.id}<span className="block text-[9px] opacity-70">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* angle type (single-wheel modes only) */}
          {mode !== "pano" && (
          <div className="rounded-md bg-surface border border-steel p-4">
            <div className="font-mono text-[10px] text-slate-400 tracking-widest uppercase mb-2">Measure</div>
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="angle-camber" onClick={() => setAngleType("camber")}
                className={`py-2 rounded-sm font-mono text-xs uppercase tracking-widest border transition-colors ${angleType === "camber" ? "bg-amber text-ink border-amber font-bold" : "bg-ink text-slate-400 border-steel hover:border-amber/50"}`}>
                Camber
              </button>
              <button data-testid="angle-toe" onClick={() => setAngleType("toe")}
                className={`py-2 rounded-sm font-mono text-xs uppercase tracking-widest border transition-colors ${angleType === "toe" ? "bg-amber text-ink border-amber font-bold" : "bg-ink text-slate-400 border-steel hover:border-amber/50"}`}>
                Toe
              </button>
            </div>
            <p className="font-mono text-[10px] text-slate-600 mt-2">
              {angleType === "toe"
                ? "Shoot from an angled/top-down view so fore-aft is visible."
                : "Shoot straight on to the wheel face for best accuracy."}
            </p>
          </div>
          )}

          {/* tilt gauge */}
          <div className="rounded-md bg-surface border border-steel p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-[10px] text-slate-400 tracking-widest uppercase flex items-center gap-1"><Smartphone size={13} /> Phone Tilt Gauge</span>
              {deviceTilt != null && <span className="font-mono text-sm text-amber tabular-nums">{deviceTilt.toFixed(1)}°</span>}
            </div>
            {!tiltEnabled ? (
              <Button data-testid="btn-enable-tilt" onClick={enableTilt} variant="outline"
                className="w-full bg-elevated border-steel text-slate-300 hover:bg-steel hover:text-white font-mono text-[11px] uppercase tracking-widest">
                Enable Tilt Sensor
              </Button>
            ) : (
              <Button data-testid="btn-use-tilt" onClick={() => setGuideAngle(Math.round((deviceTilt || 0) * 100) / 100)}
                className="w-full bg-sky/20 border border-sky/50 text-sky hover:bg-sky/30 font-mono text-[11px] uppercase tracking-widest">
                Use Tilt Reading → Guide
              </Button>
            )}
          </div>

          {mode === "camera" && (
            <div className="rounded-md border border-amber/40 bg-amber/5 p-4" data-testid="camera-help">
              <div className="font-mono text-[10px] text-amber tracking-widest uppercase mb-2">Camera won't open?</div>
              <ol className="text-[11px] text-slate-300 leading-relaxed list-decimal list-inside space-y-1">
                <li>Tap the <span className="text-amber">lock / tune icon</span> left of the address bar.</li>
                <li>Open <span className="text-amber">Permissions → Camera</span> and set it to <span className="text-amber">Allow</span>.</li>
                <li>Reload the page, then tap <span className="text-amber">Live Camera</span> again.</li>
                <li>Still stuck? Use <span className="text-amber">Upload</span> + <span className="text-amber">AI Detect</span> — same result from a photo.</li>
              </ol>
            </div>
          )}
        </div>

        {/* viewport */}
        <div className="lg:col-span-8 space-y-5">
          <div ref={viewportRef}
            className={`relative bg-ink overflow-hidden ${fullscreen ? "fixed inset-0 z-[100] rounded-none border-0 flex items-center justify-center" : "rounded-md border border-steel"}`}
            style={fullscreen ? {} : { aspectRatio: "3 / 4", maxHeight: 520 }}>
            <div className="absolute inset-0 tactical-grid opacity-30" />
            {/* camera / image layer */}
            {captured ? (
              <img src={captured} alt="scan" className={`absolute inset-0 w-full h-full ${mode === "pano" ? "object-contain bg-ink" : "object-cover"}`} data-testid="captured-image" />
            ) : (mode === "camera" || mode === "pano") ? (
              <video ref={videoRef} playsInline muted className="absolute inset-0 w-full h-full object-cover" data-testid="camera-video" />
            ) : (
              <label data-testid="upload-dropzone" className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer text-slate-500 hover:text-amber transition-colors">
                <Upload size={40} />
                <span className="font-mono text-xs tracking-widest uppercase mt-3">Tap to upload a wheel photo</span>
                <input type="file" accept="image/*" className="hidden" onChange={onUpload} data-testid="upload-input" />
              </label>
            )}

            {cameraError && !captured && (mode === "camera" || mode === "pano") && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-6 bg-ink/80" data-testid="camera-error">
                <AlertTriangle size={34} className="text-amber" />
                <p className="font-mono text-xs text-slate-300 mt-3 max-w-sm leading-relaxed">{cameraError}</p>
                <div className="flex gap-2 mt-4">
                  <Button data-testid="btn-allow-camera" onClick={startCamera} className="bg-amber text-ink font-mono text-xs uppercase tracking-widest font-bold">
                    <Camera size={15} /> Allow / Retry
                  </Button>
                  <Button data-testid="btn-switch-upload" onClick={() => setMode("upload")} variant="outline" className="bg-elevated border-steel text-slate-300 hover:bg-steel hover:text-white font-mono text-xs uppercase tracking-widest">Use Upload</Button>
                </div>
              </div>
            )}

            {/* angle guide overlay */}
            {mode !== "pano" && (mode === "upload" || !cameraError) && (
              <svg ref={svgRef} viewBox="0 0 300 400" preserveAspectRatio="none"
                className="absolute inset-0 w-full h-full touch-none cursor-grab active:cursor-grabbing"
                onPointerDown={(e) => { setDragging(true); pointerAngle(e.clientX, e.clientY); }}
                onPointerMove={(e) => dragging && pointerAngle(e.clientX, e.clientY)}
                onPointerUp={() => setDragging(false)}
                onPointerLeave={() => setDragging(false)}
                data-testid="guide-overlay">
                {/* vertical reference */}
                <line x1="150" y1="380" x2="150" y2="60" stroke="#64748B" strokeWidth="1.5" strokeDasharray="6 7" />
                {/* guide line */}
                <line x1="150" y1="380" x2={ex} y2={ey} stroke={inSpec ? "#10B981" : "#F59E0B"} strokeWidth="3" />
                <circle cx={ex} cy={ey} r="11" fill={inSpec ? "#10B981" : "#F59E0B"} fillOpacity="0.25" stroke={inSpec ? "#10B981" : "#F59E0B"} strokeWidth="2" />
                <circle cx="150" cy="380" r="5" fill={inSpec ? "#10B981" : "#F59E0B"} />
              </svg>
            )}

            {/* live readout (single-wheel modes) */}
            {mode !== "pano" && (
            <div className="absolute top-3 left-3 rounded-sm bg-ink/85 backdrop-blur-sm border border-steel px-3 py-2" data-testid="live-readout">
              <div className="font-mono text-[9px] text-slate-400 tracking-widest uppercase">{corner} · {angleType}</div>
              <div className="font-mono font-bold text-2xl tabular-nums" style={{ color: inSpec ? "#10B981" : "#F59E0B" }}>
                {guideAngle >= 0 ? "+" : ""}{guideAngle.toFixed(2)}°
              </div>
              <div className="font-mono text-[9px] tracking-widest" style={{ color: inSpec ? "#10B981" : "#EF4444" }}>
                {inSpec ? "IN SPEC" : "OUT OF SPEC"}
              </div>
              {tiltEnabled && deviceTilt != null && (
                <div className="font-mono text-[9px] text-sky tracking-widest mt-1">TILT {deviceTilt.toFixed(1)}°</div>
              )}
            </div>
            )}

            {/* panoramic sweep overlay */}
            {mode === "pano" && !captured && !cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-between p-6 pointer-events-none" data-testid="sweep-overlay">
                <div className="w-full flex items-center justify-between font-mono text-[10px] text-amber tracking-widest uppercase bg-ink/75 rounded-sm px-3 py-2">
                  <span>{axle} axle sweep</span>
                  <span data-testid="sweep-span">{sweepSpan}° swept</span>
                </div>
                <div className="flex items-center gap-2 text-amber">
                  <X size={20} className="opacity-60" />
                  <MoveHorizontal size={56} className={sweeping ? "animate-pulse" : "opacity-70"} />
                  <X size={20} className="opacity-60" />
                </div>
                <div className="w-full">
                  <div className="flex justify-between font-mono text-[9px] text-slate-200 mb-1 uppercase tracking-widest">
                    <span>Left wheel</span>
                    <span data-testid="sweep-progress">{Math.round(sweepProgress * 100)}%</span>
                    <span>Right wheel</span>
                  </div>
                  <div className="h-2 rounded-full bg-ink/70 overflow-hidden">
                    <div className="h-full bg-amber transition-all duration-200" style={{ width: `${sweepProgress * 100}%` }} />
                  </div>
                </div>
              </div>
            )}

            {/* fullscreen toggle */}
            {(mode === "camera" || captured) && !cameraError && mode !== "pano" && (
              <button onClick={toggleFullscreen} data-testid="btn-fullscreen"
                className="absolute top-3 right-3 flex items-center gap-1.5 rounded-sm bg-ink/85 backdrop-blur-sm border border-steel px-3 py-2 text-amber hover:bg-steel transition-colors font-mono text-[10px] uppercase tracking-widest">
                {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                {fullscreen ? "Exit" : "Fullscreen"}
              </button>
            )}

            {/* AI estimate chip (fullscreen) */}
            {fullscreen && aiResult && (
              <div className="absolute top-3 left-1/2 -translate-x-1/2 rounded-sm bg-sky/20 border border-sky/50 px-3 py-1.5 font-mono text-[10px] text-sky tracking-widest" data-testid="fs-ai-chip">
                AI {angleType.toUpperCase()}: {(angleType === "toe" ? aiResult.toe_deg : aiResult.camber_deg) ?? "—"}° · {aiResult.confidence != null ? Math.round(aiResult.confidence * 100) : "—"}%
              </div>
            )}

            {/* fullscreen action bar */}
            {fullscreen && (
              <div className="absolute inset-x-0 bottom-0 p-5 flex flex-wrap gap-2 justify-center bg-gradient-to-t from-ink via-ink/80 to-transparent" data-testid="fullscreen-actions">
                {mode === "camera" && !captured ? (
                  <Button data-testid="fs-capture" onClick={captureFrame} disabled={!!cameraError}
                    className="bg-amber text-ink hover:bg-amber-hover font-mono text-sm uppercase tracking-widest font-bold px-8 h-12">
                    <Aperture size={18} /> Capture
                  </Button>
                ) : captured ? (
                  <>
                    <Button data-testid="fs-retake" onClick={retake} variant="outline"
                      className="bg-elevated/80 border-steel text-slate-200 hover:bg-steel font-mono text-sm uppercase tracking-widest h-12">
                      <RotateCcw size={16} /> Retake
                    </Button>
                    <Button data-testid="fs-ai" onClick={runAI} disabled={analyzing}
                      className="bg-sky text-white hover:bg-sky/80 font-mono text-sm uppercase tracking-widest font-bold h-12 disabled:opacity-40">
                      <Sparkles size={16} /> {analyzing ? "Reading…" : "AI Read"}
                    </Button>
                    <Button data-testid="fs-apply" onClick={applyReading} disabled={applying}
                      className="bg-pass text-ink hover:bg-pass/80 font-mono text-sm uppercase tracking-widest font-bold h-12 disabled:opacity-40">
                      <Check size={16} /> {applying ? "Applying…" : "Apply"}
                    </Button>
                  </>
                ) : null}
              </div>
            )}
          </div>

          {/* fine tune slider (single-wheel modes) */}
          {mode !== "pano" && (
          <div className="rounded-md bg-surface border border-steel p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono text-[10px] text-slate-400 tracking-widest uppercase flex items-center gap-1"><Crosshair size={13} /> Fine-tune Guide Angle</span>
              <span className="font-mono text-sm text-white tabular-nums">{guideAngle.toFixed(2)}°</span>
            </div>
            <Slider data-testid="angle-slider" min={-25} max={25} step={0.05} value={[guideAngle]}
              onValueChange={(v) => setGuideAngle(v[0])} className="py-1" />
            <div className="flex justify-between font-mono text-[9px] text-slate-600 mt-1"><span>-25°</span><span>0°</span><span>+25°</span></div>
          </div>
          )}

          {/* actions */}
          {mode === "pano" ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="pano-actions">
              {!captured ? (
                sweeping ? (
                  <Button data-testid="btn-stop-sweep" onClick={finishSweep}
                    className="col-span-2 sm:col-span-3 bg-fail text-white hover:bg-fail/80 font-mono text-xs uppercase tracking-widest font-bold">
                    <Radar size={15} className="animate-spin" /> Stop Sweep ({Math.round(sweepProgress * 100)}%)
                  </Button>
                ) : (
                  <Button data-testid="btn-start-sweep" onClick={startSweep} disabled={!!cameraError}
                    className="col-span-2 sm:col-span-3 bg-amber text-ink hover:bg-amber-hover font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                    <MoveHorizontal size={15} /> Start Axle Sweep
                  </Button>
                )
              ) : (
                <>
                  <Button data-testid="btn-retake-sweep" onClick={retakeSweep} variant="outline"
                    className="bg-elevated border-steel text-slate-300 hover:bg-steel hover:text-white font-mono text-xs uppercase tracking-widest">
                    <RotateCcw size={15} /> Retake
                  </Button>
                  <Button data-testid="btn-ai-axle" onClick={runAxleAI} disabled={analyzing}
                    className="bg-sky text-white hover:bg-sky/80 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                    <Sparkles size={15} /> {analyzing ? "Reading…" : "AI Read Axle"}
                  </Button>
                  <Button data-testid="btn-apply-axle" onClick={applyAxle} disabled={applying || !axleResult}
                    className="bg-pass text-ink hover:bg-pass/80 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                    <Check size={15} /> {applying ? "Applying…" : "Apply Axle"}
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {mode === "camera" && !captured ? (
                <Button data-testid="btn-capture" onClick={captureFrame} disabled={!!cameraError}
                  className="col-span-2 sm:col-span-1 bg-amber text-ink hover:bg-amber-hover font-mono text-xs uppercase tracking-widest font-bold">
                  <Aperture size={15} /> Capture
                </Button>
              ) : (
                <Button data-testid="btn-retake" onClick={retake} variant="outline"
                  className="bg-elevated border-steel text-slate-300 hover:bg-steel hover:text-white font-mono text-xs uppercase tracking-widest">
                  <RotateCcw size={15} /> Retake
                </Button>
              )}
              <Button data-testid="btn-ai-detect" onClick={runAI} disabled={!captured || analyzing}
                className="bg-sky text-white hover:bg-sky/80 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                <Sparkles size={15} /> {analyzing ? "Scanning…" : "AI Detect"}
              </Button>
              <Button data-testid="btn-apply-reading" onClick={applyReading} disabled={applying}
                className="col-span-2 bg-pass text-ink hover:bg-pass/80 font-mono text-xs uppercase tracking-widest font-bold disabled:opacity-40">
                <Check size={15} /> {applying ? "Applying…" : "Apply to Calculator"}
              </Button>
            </div>
          )}

          {aiResult && (
            <div data-testid="ai-result" className="rounded-md border border-sky/40 bg-sky/5 p-4">
              <div className="font-mono text-[10px] text-sky tracking-widest uppercase mb-2 flex items-center gap-1"><Sparkles size={13} /> AI Estimate</div>
              <div className="grid grid-cols-3 gap-3 font-mono text-sm">
                <div><span className="text-slate-500 text-[10px] block tracking-widest">CAMBER</span><span className="text-white tabular-nums">{aiResult.camber_deg != null ? `${aiResult.camber_deg}°` : "—"}</span></div>
                <div><span className="text-slate-500 text-[10px] block tracking-widest">TOE</span><span className="text-white tabular-nums">{aiResult.toe_deg != null ? `${aiResult.toe_deg}°` : "—"}</span></div>
                <div><span className="text-slate-500 text-[10px] block tracking-widest">CONFIDENCE</span><span className="text-white tabular-nums">{aiResult.confidence != null ? `${Math.round(aiResult.confidence * 100)}%` : "—"}</span></div>
              </div>
              {aiResult.reasoning && <p className="text-xs text-slate-400 mt-3 leading-relaxed">{aiResult.reasoning}</p>}
              <p className="text-[10px] text-amber mt-2 font-mono tracking-wide">AI estimate is a rough guide — verify against gauge readings before adjusting.</p>
            </div>
          )}

          {axleResult && (
            <div data-testid="axle-result" className="rounded-md border border-sky/40 bg-sky/5 p-4">
              <div className="font-mono text-[10px] text-sky tracking-widest uppercase mb-2 flex items-center gap-1">
                <Sparkles size={13} /> AI Axle Estimate · {axle}{sweepSpan ? ` · ${sweepSpan}° sweep` : ""}
              </div>
              <div className="grid grid-cols-4 gap-3 font-mono text-sm">
                <div><span className="text-slate-500 text-[10px] block tracking-widest">L CAMBER</span><span className="text-white tabular-nums">{axleResult.left_camber_deg != null ? `${axleResult.left_camber_deg}°` : "—"}</span></div>
                <div><span className="text-slate-500 text-[10px] block tracking-widest">R CAMBER</span><span className="text-white tabular-nums">{axleResult.right_camber_deg != null ? `${axleResult.right_camber_deg}°` : "—"}</span></div>
                <div><span className="text-slate-500 text-[10px] block tracking-widest">TOTAL TOE</span><span className="text-white tabular-nums">{axleResult.total_toe_deg != null ? `${axleResult.total_toe_deg}°` : "—"}</span></div>
                <div><span className="text-slate-500 text-[10px] block tracking-widest">CONF</span><span className="text-white tabular-nums">{axleResult.confidence != null ? `${Math.round(axleResult.confidence * 100)}%` : "—"}</span></div>
              </div>
              {axleResult.reasoning && <p className="text-xs text-slate-400 mt-3 leading-relaxed">{axleResult.reasoning}</p>}
              <p className="text-[10px] text-amber mt-2 font-mono tracking-wide">Panorama AI estimate — verify against gauge readings before adjusting.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
